import React from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { getContextCategoryBadgeSx, getContextCategoryLabel } from '../../../constants/contextFolders';
import { getEntityAuthorDisplayName } from '../../../utils/userUtils';
import { colors } from '../../../designTokens';
import { getRelevanceChipProps } from './discoveryRelevanceStyles';
import { getDiscoveryMatchDetails, normalizeDiscoveryResultToContext } from './discoveryResultUtils';
import { assistantPrimaryButtonSx, assistantSecondaryButtonSx, assistantTextButtonSx } from '../assistantButtonStyles';
import { discoveryCardSx, discoveryTitleSx } from './discoveryCardStyles';
import DiscoveryCardMetadata from './DiscoveryCardMetadata';

export function KnowledgeRecommendationCard({
  result,
  kind,
  onDiscoveryAction,
  onAdd,
  onPreview,
  isBusy = false,
  featured = false,
  assistantContext = 'standard',
}) {
  const context = normalizeDiscoveryResultToContext(result);
  const item = result.context || result.raw || result;
  const chip = getRelevanceChipProps(result.relevance);
  const resolvedAuthorName = getEntityAuthorDisplayName(context);
  const rankedOwnerName = getEntityAuthorDisplayName({ author: { name: result.owner } });
  const authorName = resolvedAuthorName !== 'Unknown User'
    ? resolvedAuthorName
    : rankedOwnerName !== 'Unknown User'
      ? rankedOwnerName
      : 'PromptCrew';
  const useCount = context.engagement?.uses || context.stats?.usageCount || context.usage || result.uses || 0;
  const matchDetails = getDiscoveryMatchDetails(result, context.description);
  const knowledgeType = getContextCategoryLabel(context);
  const isBuilderContext = assistantContext === 'builder';

  const handleAction = (action, event) => {
    event?.stopPropagation?.();
    onDiscoveryAction?.({ action, item, result, kind });
  };
  const handlePreview = (event) => {
    event?.stopPropagation?.();
    if (onPreview) {
      onPreview(context, result);
    } else {
      onDiscoveryAction?.({ action: 'open', item, result, kind });
    }
  };
  const handleAdd = (event) => {
    event?.stopPropagation?.();
    if (onAdd) {
      onAdd(context, result);
    } else {
      handleAction('add', event);
    }
  };

  return (
    <Paper
      variant="outlined"
      onClick={handlePreview}
      sx={{ ...discoveryCardSx, p: featured ? 2 : 1.5 }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Chip size="small" variant="outlined" label={knowledgeType} sx={getContextCategoryBadgeSx(knowledgeType)} />
          <Chip size="small" label={!featured && Number(result.relevance) >= 5 ? 'Relevance - 5/5' : chip.label} sx={chip.sx} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="h3"
              sx={{ ...discoveryTitleSx, minHeight: 0, fontWeight: 600 }}
            >
              {context.name || context.title}
            </Typography>
            {context.description && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.45,
                  color: colors.textSecondary,
                  fontSize: '0.78125rem',
                  lineHeight: 1.45,
                  display: '-webkit-box',
                  WebkitLineClamp: featured ? 2 : 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {context.description}
              </Typography>
            )}
          </Box>
        </Box>
        {matchDetails.rationale && (
          <Box sx={{ pt: featured ? 0.1 : 0 }}>
            <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.75rem' }}>
              Why it fits
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 0.45, pl: 2.25, color: colors.textPrimary }}>
              <Typography component="li" variant="body2" sx={{ color: colors.textPrimary, fontSize: '0.8125rem', lineHeight: 1.42 }}>
                {matchDetails.rationale}
              </Typography>
            </Box>
          </Box>
        )}
        {matchDetails.adaptationGap && (
          <Box>
            <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.75rem' }}>
              Needs adaptation
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 0.45, pl: 2.25, color: colors.textPrimary }}>
              <Typography component="li" variant="body2" sx={{ color: colors.textPrimary, fontSize: '0.8125rem', lineHeight: 1.42 }}>
                {matchDetails.adaptationGap}
              </Typography>
            </Box>
          </Box>
        )}
        <DiscoveryCardMetadata
          authorName={authorName}
          author={context.author}
          uses={useCount}
          favorites={context.engagement?.favorites ?? context.favorites ?? result.favorites ?? 0}
          updatedAt={context.updatedAt || context.createdAt || result.updatedAt || result.createdAt}
        />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pt: 0.1 }}>
          <Button
            size="small"
            variant={featured ? 'contained' : 'outlined'}
            disabled={isBusy}
            onClick={handleAdd}
            sx={featured ? assistantPrimaryButtonSx : assistantSecondaryButtonSx}
          >
            {isBuilderContext ? 'Attach to Builder' : 'Add to Workspace'}
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={isBusy}
            onClick={handlePreview}
            sx={assistantTextButtonSx}
          >
            Preview
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

export default KnowledgeRecommendationCard;
